/**
 * The ACL editor's draft form (S8 Task 8.11) — TanStack Form over `{ owner, group, entries }`, mounted per loaded
 * baseline (a Reload remounts it). Access / Default scopes (Default for directories only), the entries table with the
 * Effective column, the mask controls, the owner fields, live validation (`validateFullAcl`, messages verbatim) and the
 * preview of every change before Save.
 */
import { useEffect, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';
import { Button } from '../../../../../components/ui/button';
import { ReasonButton } from '../../../../../components/ui/ReasonButton';
import { DialogFooter } from '../../../../../components/ui/dialog';
import { validateFullAcl } from '../../../../../core/engines/azureStorageAclValidate';
import type { StorageAclEntry, StorageAclScope, StorageHnsAccessControl } from '../../../../../types/azureStorage';
import { draftFromAcl, isDraftDirty, withDefaultScope, type AclEditorDraft } from '../aclDraft';
import { AclChangePreview } from './AclChangePreview';
import { AclEntriesTable } from './AclEntriesTable';
import { AclMaskControls } from './AclMaskControls';
import { AclOwnerFields } from './AclOwnerFields';

export const DEFAULT_SCOPE_NOTE = 'Default entries apply to NEW files and directories created inside this directory; use Propagate to change existing ones.';

type Props = {
  acl: StorageHnsAccessControl;
  lockedReason?: string;
  /** Why Save is off although the draft is valid (a conflict that needs Reload). */
  blockedReason?: string;
  saving: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onSave: (draft: AclEditorDraft) => void;
  onCancel: () => void;
};

export function AclEditorForm({ acl, lockedReason, blockedReason, saving, onDirtyChange, onSave, onCancel }: Props) {
  const [initial] = useState(() => draftFromAcl(acl));
  const form = useForm({ defaultValues: initial, onSubmit: ({ value }) => onSave(value) });
  const values = useStore(form.store, (state) => state.values);
  const [scope, setScope] = useState<StorageAclScope>('access');
  const dirty = isDraftDirty(values, acl);
  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  const setEntries = (next: StorageAclEntry[]) => form.setFieldValue('entries', next);
  const hasDefault = values.entries.some((e) => e.scope === 'default');
  const validation = validateFullAcl(values.entries, { isDirectory: acl.isDirectory });
  // R8 (C1): a persistent reason (read-only, a conflict to reload, an invalid ACL, nothing changed) is a ReasonButton's —
  // reachable by keyboard focus; only the short "saving" state stays a native disabled button.
  const saveReason = lockedReason ?? blockedReason ?? (validation.ok === false ? validation.reason : !dirty ? 'Nothing changed' : undefined);
  const scopeButton = (value: StorageAclScope, label: string) => (
    <Button type="button" variant={scope === value ? 'secondary' : 'ghost'} size="xs" aria-pressed={scope === value} onClick={() => setScope(value)} data-testid={`hns-acl-scope-${value}`}>{label}</Button>
  );
  return (
    <form className="grid gap-3" onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); if (!saveReason && !saving) void form.handleSubmit(); }}>
      <AclOwnerFields owner={values.owner} group={values.group} loadedOwner={acl.owner} loadedGroup={acl.group} permissions={acl.permissions} stickyBit={acl.stickyBit} superUser={acl.superUser} lockedReason={lockedReason}
        onOwnerChange={(value) => form.setFieldValue('owner', value)} onGroupChange={(value) => form.setFieldValue('group', value)} />
      {acl.isDirectory && <div className="flex items-center gap-1" role="group" aria-label="Scope">{scopeButton('access', 'Access')}{scopeButton('default', 'Default')}</div>}
      {scope === 'default' && <p className="text-xs text-muted-foreground" data-testid="hns-acl-default-note">{DEFAULT_SCOPE_NOTE}</p>}
      {scope === 'default' && !hasDefault ? (
        <Button type="button" variant="outline" size="xs" className="w-fit" disabled={!!lockedReason} title={lockedReason} onClick={() => setEntries(withDefaultScope(values.entries))} data-testid="hns-acl-add-default">Add a default ACL</Button>
      ) : (
        <div className="max-h-[40vh] overflow-auto">
          <AclEntriesTable entries={values.entries} scope={scope} displayNames={acl.displayNames} lockedReason={lockedReason} onChange={setEntries} />
        </div>
      )}
      <AclMaskControls entries={values.entries} scope={scope} lockedReason={lockedReason} onChange={setEntries} />
      {scope === 'default' && hasDefault && <Button type="button" variant="ghost" size="xs" className="w-fit" disabled={!!lockedReason} title={lockedReason} onClick={() => setEntries(values.entries.filter((e) => e.scope !== 'default'))} data-testid="hns-acl-remove-default">Remove the default ACL</Button>}
      {validation.ok === false && dirty && <p className="text-xs text-destructive" role="alert" data-testid="hns-acl-validation">{validation.reason}</p>}
      {dirty && <AclChangePreview before={{ owner: acl.owner, group: acl.group, entries: acl.entries }} after={values} displayNames={acl.displayNames} title="Changes to save" />}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="hns-acl-cancel">{lockedReason ? 'Close' : 'Cancel'}</Button>
        <ReasonButton type="submit" reason={saveReason} disabled={saving} data-testid="hns-acl-save">{saving ? 'Saving…' : 'Save'}</ReasonButton>
      </DialogFooter>
    </form>
  );
}
